import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Plus, X, Copy, FileText } from 'lucide-react';
import { generateReport, getAllowedTags } from '@/api/report';
import { reportToText, copyToClipboard } from '@/utils/reportToText';

interface TaggedItem {
  text: string;
  tag: string;
  id: string;
}

interface ReportSections {
  executiveSummary: string[];
  context: string[];
  researchInsights: string[];
  analysis: string[];
  recommendations: string[];
  roadmap: {
    now: string[];
    next: string[];
    later: string[];
  };
  appendix: string[];
}

interface ReportData {
  sections: ReportSections;
  meta: {
    generatedAt: string;
    itemCounts: Record<string, number>;
    totalItems: number;
  };
}

export default function ReportBuilderV1() {
  const { toast } = useToast();
  
  // State management
  const [transcript, setTranscript] = useState('');
  const [items, setItems] = useState<TaggedItem[]>([]);
  const [newItemText, setNewItemText] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [allowedTags, setAllowedTags] = useState<string[]>([]);
  const [report, setReport] = useState<ReportData | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingTags, setIsLoadingTags] = useState(true);

  // Load allowed tags on component mount
  useEffect(() => {
    async function loadTags() {
      try {
        const tags = await getAllowedTags();
        setAllowedTags(tags);
      } catch (error) {
        toast({
          title: 'Failed to load tags',
          description: 'Could not fetch allowed tags from server',
          variant: 'destructive',
        });
      } finally {
        setIsLoadingTags(false);
      }
    }
    loadTags();
  }, [toast]);

  // Add new tagged item
  const handleAddItem = () => {
    if (!newItemText.trim() || !selectedTag) {
      toast({
        title: 'Missing information',
        description: 'Please enter both text and select a tag',
        variant: 'destructive',
      });
      return;
    }

    const newItem: TaggedItem = {
      id: Date.now().toString(),
      text: newItemText.trim(),
      tag: selectedTag,
    };

    setItems(prev => [...prev, newItem]);
    setNewItemText('');
    setSelectedTag('');
    
    toast({
      title: 'Item added',
      description: `Added "${newItem.text}" with tag "${newItem.tag}"`,
    });
  };

  // Remove tagged item
  const handleRemoveItem = (id: string) => {
    setItems(prev => prev.filter(item => item.id !== id));
    toast({
      title: 'Item removed',
      description: 'Tagged item has been removed',
    });
  };

  // Generate report
  const handleGenerateReport = async () => {
    if (items.length === 0) {
      toast({
        title: 'No items to analyse',
        description: 'Please add at least one tagged item before generating a report',
        variant: 'destructive',
      });
      return;
    }

    setIsGenerating(true);
    try {
      const reportData = await generateReport({
        transcript: transcript.trim(),
        items: items,
      });
      
      setReport(reportData);
      toast({
        title: 'Report generated successfully',
        description: `Analysed ${items.length} items across ${Object.keys(reportData.meta.itemCounts).length} categories`,
      });
    } catch (error: any) {
      toast({
        title: 'Report generation failed',
        description: error.message || 'An error occurred while generating the report',
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Copy report as plain text
  const handleCopyReport = async () => {
    if (!report) return;

    const plainText = reportToText(report);
    const success = await copyToClipboard(plainText);
    
    if (success) {
      toast({
        title: 'Report copied',
        description: 'Report has been copied to clipboard as plain text',
      });
    } else {
      toast({
        title: 'Copy failed',
        description: 'Could not copy report to clipboard',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight">Report Builder v1</h1>
          <p className="text-muted-foreground mt-2">
            Transform your research into structured, actionable reports
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Panel - Input */}
          <div className="space-y-6">
            {/* Transcript Input */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Conversation Transcript
                </CardTitle>
                <CardDescription>
                  Paste your conversation transcript here (optional)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Paste your conversation transcript here..."
                  value={transcript}
                  onChange={(e) => setTranscript(e.target.value)}
                  className="min-h-[120px] resize-none"
                />
              </CardContent>
            </Card>

            {/* Tagging Interface */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="h-5 w-5" />
                  Add Tagged Items
                </CardTitle>
                <CardDescription>
                  Tag key insights, data points, and action items
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="item-text">Item Text</Label>
                  <Textarea
                    id="item-text"
                    placeholder="Enter the text you want to tag..."
                    value={newItemText}
                    onChange={(e) => setNewItemText(e.target.value)}
                    className="min-h-[80px] resize-none"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="tag-select">Tag</Label>
                  <Select value={selectedTag} onValueChange={setSelectedTag} disabled={isLoadingTags}>
                    <SelectTrigger>
                      <SelectValue placeholder={isLoadingTags ? "Loading tags..." : "Select a tag"} />
                    </SelectTrigger>
                    <SelectContent>
                      {allowedTags.map((tag) => (
                        <SelectItem key={tag} value={tag}>
                          {tag}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  onClick={handleAddItem} 
                  className="w-full"
                  disabled={!newItemText.trim() || !selectedTag || isLoadingTags}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Item
                </Button>
              </CardContent>
            </Card>

            {/* Current Items */}
            <Card>
              <CardHeader>
                <CardTitle>Current Items ({items.length})</CardTitle>
                <CardDescription>
                  Review and manage your tagged items
                </CardDescription>
              </CardHeader>
              <CardContent>
                {items.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No items added yet. Add some tagged items above to get started.
                  </p>
                ) : (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {items.map((item) => (
                      <div key={item.id} className="flex items-start gap-3 p-3 border rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium mb-1">{item.text}</p>
                          <Badge variant="secondary" className="text-xs">
                            {item.tag}
                          </Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveItem(item.id)}
                          className="text-muted-foreground hover:text-destructive"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Generate Button */}
            <Button 
              onClick={handleGenerateReport} 
              disabled={items.length === 0 || isGenerating}
              className="w-full h-12"
              size="lg"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating Report...
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  Generate Report
                </>
              )}
            </Button>
          </div>

          {/* Right Panel - Output */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Generated Report</CardTitle>
                    <CardDescription>
                      {report ? `Generated ${new Date(report.meta.generatedAt).toLocaleString('en-GB')}` : 'Your report will appear here'}
                    </CardDescription>
                  </div>
                  {report && (
                    <Button variant="outline" onClick={handleCopyReport}>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy as Text
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {!report ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Generate a report to see the structured output here</p>
                  </div>
                ) : (
                  <div className="space-y-6 max-h-[800px] overflow-y-auto">
                    {/* Executive Summary */}
                    {report.sections.executiveSummary.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-lg mb-3">Executive Summary</h3>
                        <ul className="space-y-2">
                          {report.sections.executiveSummary.map((item, index) => (
                            <li key={index} className="text-sm leading-relaxed">• {item}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Context & Objective */}
                    {report.sections.context.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-lg mb-3">Context & Objective</h3>
                        <ul className="space-y-2">
                          {report.sections.context.map((item, index) => (
                            <li key={index} className="text-sm leading-relaxed">• {item}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Research Insights */}
                    {report.sections.researchInsights.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-lg mb-3">Research Insights</h3>
                        <ul className="space-y-2">
                          {report.sections.researchInsights.map((item, index) => (
                            <li key={index} className="text-sm leading-relaxed">• {item}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Analysis & Interpretation */}
                    {report.sections.analysis.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-lg mb-3">Analysis & Interpretation</h3>
                        <ul className="space-y-2">
                          {report.sections.analysis.map((item, index) => (
                            <li key={index} className="text-sm leading-relaxed">• {item}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Recommendations */}
                    {report.sections.recommendations.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-lg mb-3">Recommendations</h3>
                        <ul className="space-y-2">
                          {report.sections.recommendations.map((item, index) => (
                            <li key={index} className="text-sm leading-relaxed">{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Implementation Roadmap */}
                    {(report.sections.roadmap.now.length > 0 || report.sections.roadmap.next.length > 0 || report.sections.roadmap.later.length > 0) && (
                      <div>
                        <h3 className="font-semibold text-lg mb-3">Implementation Roadmap</h3>
                        
                        {report.sections.roadmap.now.length > 0 && (
                          <div className="mb-4">
                            <h4 className="font-medium text-base mb-2">Now (Immediate)</h4>
                            <ul className="space-y-1 ml-4">
                              {report.sections.roadmap.now.map((item, index) => (
                                <li key={index} className="text-sm leading-relaxed">• {item}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {report.sections.roadmap.next.length > 0 && (
                          <div className="mb-4">
                            <h4 className="font-medium text-base mb-2">Next (Short-term)</h4>
                            <ul className="space-y-1 ml-4">
                              {report.sections.roadmap.next.map((item, index) => (
                                <li key={index} className="text-sm leading-relaxed">• {item}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {report.sections.roadmap.later.length > 0 && (
                          <div>
                            <h4 className="font-medium text-base mb-2">Later (Long-term)</h4>
                            <ul className="space-y-1 ml-4">
                              {report.sections.roadmap.later.map((item, index) => (
                                <li key={index} className="text-sm leading-relaxed">• {item}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Appendix/Sources */}
                    {report.sections.appendix.length > 0 && (
                      <div>
                        <h3 className="font-semibold text-lg mb-3">Appendix/Sources</h3>
                        <ul className="space-y-2">
                          {report.sections.appendix.map((item, index) => (
                            <li key={index} className="text-sm leading-relaxed">• {item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
